import { useState } from 'react';

import {
  Anchor,
  Badge,
  Card,
  Collapse,
  Group,
  List,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

import type {
  MatchReviewDecision,
  MatchReviewReason,
  RemediationReview,
} from '@/types/match-review';
import { ReviewVerdictControls, type ReviewVerdict } from '../ReviewVerdict';
import classes from './MatchReviewCard.module.css';

export interface MatchReviewCardProps {
  review: RemediationReview;
  /** Verdict buttons are inert while a mutation is in flight or the row
   * has already been applied. */
  disabled?: boolean;
  onDecide: (decision: MatchReviewDecision, note?: string | null) => void | Promise<void>;
}

/** The verdict pair as the analyst sees it; the values are the API's. */
const REJECT_VERDICT: ReviewVerdict<MatchReviewDecision> = {
  value: 'reject_match',
  label: 'Not the subject',
  testId: 'reject-match',
};
const CONFIRM_VERDICT: ReviewVerdict<MatchReviewDecision> = {
  value: 'confirm_match',
  label: 'Confirm match',
  testId: 'confirm-match',
};

const REASON_CHIPS: Record<MatchReviewReason, { label: string; color: string }> = {
  low_confidence_match: { label: 'Low-confidence match', color: 'orange' },
  uncertain_non_match: { label: 'Uncertain non-match', color: 'yellow' },
  cross_validation_demotion: { label: 'Removed by cross-validation', color: 'violet' },
  retained_match: { label: 'Retained match', color: 'blue' },
};

function providerLabel(provider: string): string {
  return provider
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatPercent(value: number): string {
  return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
}

/** One queued remediation record: what it is, what the AI made of it,
 * and the analyst's verdict. The verdict controls sit in the header so a
 * card can be decided without scrolling past its detail. */
export function MatchReviewCard({ review, disabled = false, onDecide }: MatchReviewCardProps) {
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [note, setNote] = useState(review.note ?? '');

  const chip = REASON_CHIPS[review.reviewReason] ?? {
    label: review.reviewReason.replace(/_/g, ' '),
    color: 'gray',
  };
  const aiSaysMatch = review.aiDecision === 'true_positive';
  const applied = review.appliedAt !== null;
  const inert = disabled || applied;
  const { summary } = review;
  const facts = summary.key_facts ?? [];

  const decide = (decision: MatchReviewDecision) => onDecide(decision, note.trim() || null);

  return (
    <Card
      withBorder
      padding="sm"
      className={classes.card}
      data-decision={review.analystDecision ?? undefined}
      data-testid={`match-review-card-${review.id}`}
    >
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start" gap="xs">
          <Group gap="xs" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
            <Badge variant="outline" color="gray" size="sm">
              {providerLabel(review.provider)} · {review.recordType.replace(/-/g, ' ')}
            </Badge>
            <Badge variant="light" color={chip.color} size="sm">
              {chip.label}
            </Badge>
            <Badge variant="filled" color={aiSaysMatch ? 'teal' : 'gray'} size="sm">
              AI: {aiSaysMatch ? 'match' : 'not a match'} ·{' '}
              {formatPercent(review.aiMatchConfidence)}
            </Badge>
          </Group>
          <ReviewVerdictControls
            reject={REJECT_VERDICT}
            confirm={CONFIRM_VERDICT}
            decision={review.analystDecision}
            disabled={inert}
            onDecide={decide}
          />
        </Group>

        <div>
          <Text fw={600} className={classes.name}>
            {summary.name || 'Unnamed record'}
          </Text>
          {summary.jurisdictions && summary.jurisdictions.length > 0 && (
            <Text size="xs" c="dimmed">
              {summary.jurisdictions.join(', ')}
            </Text>
          )}
          {review.variantUsed && review.variantUsed !== 'canonical' && (
            <Text size="xs" c="dimmed">
              Found under alternate spelling “{review.variantUsed}”
            </Text>
          )}
        </div>

        {facts.length > 0 && (
          <List size="sm" spacing={2} className={classes.facts}>
            {facts.map((fact) => (
              <List.Item key={fact}>{fact}</List.Item>
            ))}
          </List>
        )}

        {summary.source_url && (
          <Anchor href={summary.source_url} target="_blank" rel="noopener noreferrer" size="sm">
            Open source
          </Anchor>
        )}

        <div>
          <UnstyledButton
            onClick={() => setReasoningOpen((o) => !o)}
            className={classes.reasoningToggle}
            aria-expanded={reasoningOpen}
          >
            <Group gap={4}>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                AI reasoning
              </Text>
              {reasoningOpen ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
            </Group>
          </UnstyledButton>
          <Collapse in={reasoningOpen}>
            <Text size="sm" className={classes.reasoning}>
              {review.aiReasoning || 'No reasoning recorded.'}
            </Text>
          </Collapse>
        </div>

        <Group gap="xs" wrap="nowrap" align="center">
          <TextInput
            size="xs"
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            disabled={inert}
            aria-label="Review note"
            style={{ flex: 1 }}
          />
          {applied && (
            <Text size="xs" c="dimmed">
              Applied
            </Text>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
